import {
  ChevronRight,
  LayoutDashboard,
  Settings,
  HandCoins,
  BadgeDollarSign,
  Bug,
  StretchVertical,
  Smartphone,
  CircleUser,
  LogOut,
  CircleChevronDown,
  Package,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { ProtectedRoutes, PublicRoutes } from "../../types/Routes";
import { AccessToken } from "../../enums/access-token";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from "../ui/Sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/DropDownMenu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/Collapsible";
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/Avatar";
import Navbar from "../Navbar";
import { logoFull } from "../../assets";

const SidebarLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const handleLogout = () => {
    localStorage.removeItem(AccessToken.KEY);
    window.location.href = PublicRoutes.SIGNIN;
  };
  const location = useLocation();
  const pathname = location.pathname;

  const data = {
    user: {
      name: "shadcn",
      email: "m@example.com",
      avatar: "/avatars/shadcn.jpg",
    },
    navMain: [
      {
        title: "Dashboard",
        url: ProtectedRoutes.DASHBOARD,
        icon: LayoutDashboard,
        active: pathname === ProtectedRoutes.DASHBOARD,
      },
      {
        title: "Products",
        url: "#",
        icon: Package,
        active: pathname
          .split("/")
          .some(segment =>
            ["products", "orders", "inventory"].includes(segment)
          ),
        items: [
          {
            title: "Overview",
            url: ProtectedRoutes.PRODUCTS,
            active: pathname === ProtectedRoutes.PRODUCTS,
          },
          {
            title: "All Products",
            url: ProtectedRoutes.ALL_PRODUCTS,
            active: pathname === ProtectedRoutes.ALL_PRODUCTS,
          },
          {
            title: "Inventory Management",
            url: ProtectedRoutes.INVENTORY,
            active: pathname === ProtectedRoutes.INVENTORY,
          },
          {
            title: "Orders",
            url: ProtectedRoutes.ORDERS,
            active: pathname
              .split("/")
              .some(segment => ["orders"].includes(segment)),
          },
        ],
      },
      {
        title: "Devices",
        url: "#",
        icon: Smartphone,
        active: pathname
          .split("/")
          .some(segment =>
            ["model", "brand", "device", "devices"].includes(segment)
          ),
        items: [
          {
            title: "Devices",
            url: ProtectedRoutes.DEVICES,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(ProtectedRoutes.DEVICES.split("/").pop() || ""),
          },
          {
            title: "Brands",
            url: ProtectedRoutes.BRANDS,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(ProtectedRoutes.BRANDS.split("/").pop() || ""),
          },
          {
            title: "Models",
            url: ProtectedRoutes.MODELS,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(ProtectedRoutes.MODELS.split("/").pop() || ""),
          },
        ],
      },
      {
        title: "Distributors",
        url: "#",
        icon: StretchVertical,
        active: pathname
          .split("/")
          .some(segment =>
            ["registration-request", "manage-distributors"].includes(segment)
          ),
        items: [
          {
            title: "Manage Distributors",
            url: ProtectedRoutes.MANAGE_DISTRUBUTORS,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(
                ProtectedRoutes.MANAGE_DISTRUBUTORS.split("/").pop() || ""
              ),
          },
          {
            title: "Registration Requests",
            url: ProtectedRoutes.REGISTRATION_REQUEST,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(
                ProtectedRoutes.REGISTRATION_REQUEST.split("/").pop() || ""
              ),
          },
        ],
      },
      {
        title: "Customers",
        url: ProtectedRoutes.CUSTOMERS,
        icon: CircleUser,
        active: pathname
          .split("/")
          .some(segment => ["customers"].includes(segment)),
      },
      {
        title: "Transactions",
        url: ProtectedRoutes.TRANSACTIONS,
        icon: BadgeDollarSign,
        active: pathname === ProtectedRoutes.TRANSACTIONS,
      },
      {
        title: "Payments",
        url: "#",
        icon: HandCoins,
        active: pathname
          .split("/")
          .some(segment =>
            ["payment-plans", "payment-bonuses"].includes(segment)
          ),
        items: [
          {
            title: "Payment Plans",
            url: ProtectedRoutes.PAYMENT_PLANS,
            active: pathname === ProtectedRoutes.PAYMENT_PLANS,
          },
          {
            title: "Bonus Templates",
            url: ProtectedRoutes.PAYMENT_BONUSES,
            active: pathname === ProtectedRoutes.PAYMENT_BONUSES,
          },
        ],
      },
      {
        title: "Activation Debug",
        url: ProtectedRoutes.ACTIVATION_DEBUG,
        icon: Bug,
        active: pathname.startsWith(ProtectedRoutes.ACTIVATION_DEBUG),
      },
      {
        title: "Settings",
        url: "#",
        icon: Settings,
        active: pathname
          .split("/")
          .some(segment =>
            ["profile", "messages", "account-plans", "kyc"].includes(segment)
          ),
        items: [
          {
            title: "Profile",
            url: ProtectedRoutes.PROFILE,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(ProtectedRoutes.PROFILE.split("/").pop() || ""),
          },
          {
            title: "Account Plans",
            url: ProtectedRoutes.ACCOUNT_PLANS,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(ProtectedRoutes.ACCOUNT_PLANS.split("/").pop() || ""),
          },
          {
            title: "Donation Networks",
            url: ProtectedRoutes.DONATION_NETWORKS,
            active: pathname
              .split("/")
              .filter(segment => segment && segment.length > 2)
              .includes(
                ProtectedRoutes.DONATION_NETWORKS.split("/").pop() || ""
              ),
          },
          // {
          //   title: "Messages",
          //   url: ProtectedRoutes.MESSAGES,
          //   active: pathname
          //     .split("/")
          //     .filter((segment) => segment && segment.length > 2)
          //     .includes(ProtectedRoutes.MESSAGES.split("/").pop() || ""),
          // },
        ],
      },
    ],
  };
  return (
    <SidebarProvider>
      <Sidebar variant="inset">
        <SidebarHeader className="">
          <SidebarMenu>
            <SidebarMenuItem className="border-b py-[7px] border-gray300">
              <Link className="" to="/">
                <img
                  src={logoFull}
                  alt="Eazipower Logo"
                  className="mb-5 h-121px"
                />
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {data.navMain.map(item =>
                item.items ? (
                  <Collapsible
                    key={item.title}
                    asChild
                    defaultOpen={item.active}
                  >
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        className={`px-[14px] py-[11px] text-textDark rounded-[6px] h-[46px]`}
                        isActive={item.active}
                        tooltip={item.title}
                      >
                        <Link to={item.url}>
                          <item.icon className={""} />
                          <span className={""}>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction className="data-[state=open]:rotate-90">
                          <ChevronRight
                            className=""
                            color={
                              item.active ? "#ffff" : "rgba(81, 81, 81, 1)"
                            }
                          />
                          <span className="sr-only">Toggle</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items?.map(subItem => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                isActive={subItem.active}
                                asChild
                              >
                                <Link to={subItem.url}>
                                  <span className={""}>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.active}
                      tooltip={item.title}
                      className={`px-[14px] py-[11px] text-text_color_00 rounded-[6px] h-[46px]`}
                    >
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              )}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton className="h-[56px]" isActive size={"lg"}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <Avatar className="w-[36px] h-[36px] cursor-pointer rounded-xl border-[1.5px] border-yellow_00">
                          <AvatarImage src="https://github.com/shadcn.png" />
                          <AvatarFallback></AvatarFallback>
                        </Avatar>
                        <div className="space-y-0.5">
                          <p className="text-white">Promise Uka</p>
                          <p className="text-[#ffff] text-xs font-[300]">
                            Super Admin
                          </p>
                        </div>
                      </div>
                      <CircleChevronDown color="#ffff" width={16} height={16} />
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width]  border min-w-56 rounded-lg"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <Avatar className="w-[36px] h-[36px] cursor-pointer rounded-xl border-[1.5px] border-yellow_00">
                          <AvatarImage src="https://github.com/shadcn.png" />
                          <AvatarFallback></AvatarFallback>
                        </Avatar>
                        <div className="space-y-0.5">
                          <p className="text-white">Promise Uka</p>
                          <p className="text-textDark text-xs font-[300]">
                            Super Admin
                          </p>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="" />
                  <DropdownMenuItem
                    className="text-white"
                    onClick={handleLogout}
                  >
                    <LogOut />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <Navbar />
        <div>{children}</div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SidebarLayout;
